<template>
  <u-deploy-panel :selected="true" :sideinfo="true">
    <template slot="title">
      {{ $t('title') }}
    </template>
    <template slot="content">
      <v-form ref="form" v-model="isFormValid">
        <v-alert v-if="warning" type="warning" class="mt-5" border="left" colored-border>
          <i18n path="warning_namespace">
            <a place="here" :href="docsWarn" target="_blank">{{ $t('here') }}</a>
          </i18n>
        </v-alert>
        <u-namespace-combobox
          @fetchTokens="fetchTokens(namespace)"
          @namespaceExist="setNamespaceExist(true)"
        />
        <v-row>
          <v-col>
            <v-text-field
              v-if="currentTab === 'compose'"
              :label="$t('id.label')"
              :hint="$t('id.hint')"
              :rules="deploymentNameRules"
              @input="setDeploymentName"
              persistent-hint
              required
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-if="device.password && namespace && currentTab === 'compose'"
              class="mb-4"
              :value="adminPassword"
              :label="$t('admin_password.label')"
              :hint="$t('admin_password.hint')"
              :append-icon="showPassword ? 'visibility_off' : 'visibility'"
              @click:append="() => (showPassword = !showPassword)"
              :type="showPassword ? 'text' : 'password'"
              persistent-hint
              readonly
            />
          </v-col>
        </v-row>
        <v-tabs
          class="commandTabs"
          dark
        >
          <v-tab
            v-for="tab in deployTabs"
            :key="tab.id"
            @change="updateTab(tab.id)"
          >
            {{ tab.title }}
          </v-tab>
          <v-tab-item class="command" key="vApp">
            <kbd v-if="namespaceExist">
              <div v-if="!pending">
                <a :href="vAppDownloadURL" target="_blank">{{ $t('vApp.download') }}</a>
                <p>{{ vApp }}</p>
              </div>
              <div class="pending" v-else>
                <v-progress-circular
                  :size="70"
                  :width="7"
                  color="secondary"
                  indeterminate
                ></v-progress-circular>
              </div>
            </kbd>
            <kbd v-else>{{ $t('v_app_placeholder') }}</kbd>
            <v-btn depressed class="clipboard" v-clipboard:copy="vApp" :disabled="!namespaceExist" large>
              <v-icon>content_copy</v-icon>
            </v-btn>
          </v-tab-item>
          <v-tab-item class="command" key="compose">
            <kbd v-if="isFormValid">
              <div v-if="!pending">
                <template v-if="namespaceExist">{{ dockerCompose }}</template>
                <template v-else>{{ $t('docker.namespace_warning') }}</template>
              </div>
              <div class="pending" v-else>
                <v-progress-circular
                  :size="70"
                  :width="7"
                  color="secondary"
                  indeterminate
                ></v-progress-circular>
              </div>
            </kbd>
            <kbd v-else>{{ $t('docker_compose_placeholder') }}</kbd>
            <v-btn depressed class="clipboard" v-clipboard:copy="dockerCompose" :disabled="!isFormValid || !namespaceExist" large>
              <v-icon>content_copy</v-icon>
            </v-btn>
          </v-tab-item>
          <v-tab-item class="command" key="kubernetes">
            <kbd>{{ kubernetes }}</kbd>
            <v-btn depressed class="clipboard" v-clipboard:copy="kubernetes" :disabled="!isFormValid || !namespaceExist" large>
              <v-icon>content_copy</v-icon>
            </v-btn>
          </v-tab-item>
        </v-tabs>
      </v-form>
    </template>
    <template slot="actions">
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('cancel')">{{ $t('wizard.cancel') }}</v-btn>
      <v-btn text @click="$emit('prev')">{{ $t('wizard.prev') }}</v-btn>
      <v-btn color="primary" :disabled="!isFormValid" @click="$emit('next')">{{ $t('complete') }}</v-btn>
    </template>
    <template slot="info">
      <v-tab ripple>{{ tabs.info.title }}</v-tab>
      <v-tab-item>
        <div>
          <u-deploy-info
            class="mt-3"
            :info="$t('headline')"
          ></u-deploy-info>
          <u-deploy-info
            class="mt-3"
            :title="$t('tabs.info.vApp.title')"
            :info="$t('tabs.info.vApp.points')"
            :documentation="$t('tabs.info.vApp.doc')"
          ></u-deploy-info>
          <u-deploy-info
            class="mt-3"
            :title="$t('tabs.info.compose.title')"
            :info="$t('tabs.info.compose.points')"
            :documentation="$t('tabs.info.compose.doc')"
          ></u-deploy-info>
          <u-deploy-info
            class="mt-3 mb-3"
            :title="$t('tabs.info.kubernetes.title')"
            :documentation="$t('tabs.info.kubernetes.doc')"
          ></u-deploy-info>
          <i18n class="mt-1" path="tabs.info.faq">
            <a place="faq" :href="docs" target="_blank">{{ $t('installation') }}</a>
          </i18n>
        </div>
      </v-tab-item>
      <v-tab ripple>
        {{ tabs.detected.title }}
        <p class="accent--text">({{ listAddedDevices.length }})</p>
      </v-tab>
      <v-tab-item>
        <u-deploy-info
          class="mt-3 ml-3"
          :title="$t('tabs.detected.title')"
        ></u-deploy-info>
        <div class="ml-3" v-if="listAddedDevices.length === 0">{{ this.$t('tabs.detected.text') }}</div>
        <v-list class="transparent ml-3 mb-2" two-line>
          <v-list-tile-content class="mb-1" v-for="device in listAddedDevices" :key="device.id">
            <v-list-tile-title class="caption font-weight-bold">{{ $t('display_name', { displayName: device.label }) }}</v-list-tile-title>
            <ul>
              <li class="accent--text">{{ $t('connected') }}</li>
            </ul>
          </v-list-tile-content>
        </v-list>
        <v-card-text>
          <i18n path="tabs.namespaces.text">
            <span class="accent--text" place="numberOfDevice">{{ listNamespaceDevices.length }}</span>
            <span class="accent--text" place="namespace">{{ namespace || '' }}</span>
          </i18n>
        </v-card-text>
        <v-list class="transparent ml-3 mb-2" two-line>
          <v-list-tile-content class="mb-1" v-for="device in listNamespaceDevices" :key="device.id">
            <v-list-tile-title class="caption font-weight-bold">{{ $t('display_name', { displayName: device.label }) }}</v-list-tile-title>
            <ul>
              <li class="accent--text">{{ $t('connected') }}</li>
            </ul>
          </v-list-tile-content>
        </v-list>
      </v-tab-item>
    </template>
  </u-deploy-panel>
</template>

<script>
import NetworkService from '@/services/network'
import DeployInfoComponent from '@/components/deploy/Info.vue'
import DeployPanelComponent from '@/components/deploy/Panel.vue'
import NamespaceComponent from '@/components/deploy/Namespace.vue'
import { generateDockerCompose } from '@/services/compose'
import { generateKubernetesCommand } from '@/services/kubernetes'
import { docsUrl } from '@/services/docs'
import { NodeStatus } from '@/model/node'
import { tokenBodies, tokenFindRules, TokenType, TokenUser } from '@/model/token'

export default {
  data () {
    return {
      operatingSystem: '',
      token: '',
      vaultToken: '',
      isFormValid: false,
      adminPassword: '',
      showPassword: true,
      deploymentNameRules: [
        (d) => !!d || this.$t('error.deploymentName_required'),
        (d) => this.validateDeploymentName(d) || this.$t('error.deploymentName_invalid')
      ],
      currentTab: 'vapp',
      deployTabs: [
        { id: 'vApp', title: this.$t('vApp.title') },
        { id: 'compose', title: this.$t('docker-compose.title') },
        { id: 'kubernetes', title:  this.$t('kubernetes.title') }
      ],
      tabs: {
        info: { title: this.$t('tabs.info.title'), text: this.$t('tabs.info.text') },
        namespaces: { title: this.$t('tabs.namespaces.title') },
        detected: { title: this.$t('tabs.detected.title'), text: this.$t('tabs.detected.text') }
      },
      listNamespaceDevices: [],
      lastSelectedNamespace: null,
      listAddedDevices: [],
      pending: false,
      namespaceExist: false,
      docs: docsUrl('faq'),
      docsWarn: docsUrl('quickstart'),
      warning: false,
      vAppAIODownloadURL: process.env.VUE_APP_VMWARE_AIO_APP_URL,
      vAppCollectorDownloadURL: process.env.VUE_APP_VMWARE_COLLECTOR_APP_URL
    }
  },
  computed: {
    wizard () {
      return this.$store.state.wizard
    },
    device () {
      return this.wizard.selectedDevice
    },
    location () {
      return this.wizard.selectedLocation
    },
    namespace () {
      return this.wizard.selectedNamespace
    },
    deploymentName () {
      return this.wizard.deploymentName
    },
    vApp () {
      return [
        `${this.$t('vApp.info')}`,
        `${this.$t('vApp.token')} ${this.token}`,
        `${this.$t('vApp.vaultToken')} ${this.vaultToken}`
      ].join('\n')
    },
    vAppDownloadURL () {
      if (this.wizard.selectedDevice.id === 'collector') return this.vAppCollectorDownloadURL
      return this.vAppAIODownloadURL
    },
    kubernetes () {
      const device = this.device.id
      return generateKubernetesCommand(device, this.token, this.vaultToken)
    }
  },
  asyncComputed: {
    dockerCompose () {
      if (this.isFormValid) {
        this.adminPassword = this.wizard.adminPassword
        const device = this.device.id
        const uuid = this.wizard.uuid
        return generateDockerCompose(device, this.token, this.vaultToken, uuid, this.deploymentName, this.adminPassword)
      }
    }
  },
  created () {
    this.fetchDevicesInterval = setInterval(this.fetchAddedDevices, 5000)
  },
  updated () {
    const { selectedNamespace } = this.wizard
    if (selectedNamespace && selectedNamespace !== this.lastSelectedNamespace) {
      this.fetchNamespaceDevices(selectedNamespace)
      this.lastSelectedNamespace = selectedNamespace
    }
    if (this.device.warnIfNotEmpty && this.listNamespaceDevices.length > 0) {
      this.warning = true
    } else {
      this.warning = false
    }
  },
  destroyed () {
    clearInterval(this.fetchDevicesInterval)
  },
  methods: {
    fetchTokens (namespace) {
      return Promise.all([
        this.fetchToken(namespace, TokenUser.NODE),
        this.fetchToken(namespace, TokenUser.VAULT)]
      ).then((tokens) => {
        this.token = tokens[0]
        this.vaultToken = tokens[1]
      })
    },
    fetchToken (namespace, user) {
      this.pending = true
      const { tokenHistory } = this.wizard
      if (!tokenHistory[user][namespace] && namespace) {
        return NetworkService.namespaceTokens(namespace).then((data) => {
          const token = data.find(tokenFindRules[user])
          if (token !== undefined) {
            return token
          } else {
            const body = tokenBodies[user]
            return NetworkService.createToken(namespace, body)
          }
        }).then((data) => {
          tokenHistory[user][namespace] = data.token
          return data.token
        }).catch(() => {
          this.namespaceExist = false
        }).finally(() => {
          this.pending = false
        })
      } else {
        this.pending = false
        return Promise.resolve(tokenHistory[user][namespace])
      }
    },
    fetchNamespaceDevices (namespace) {
      NetworkService.nodes(namespace).then((data) => {
        this.listNamespaceDevices = data.filter((device) => {
          return device.health.status === NodeStatus.ONLINE
        })
      })
    },
    fetchAddedDevices () {
      const filter = this.wizard.uuid
      NetworkService.nodes(this.namespace).then((data) => {
        this.listAddedDevices = data.filter((device) => {
          return device.id.includes(filter)
        })
      })
    },
    setNamespaceExist (namespaceExist) {
      this.namespaceExist = namespaceExist
    },
    setDeploymentName (deploymentName) {
      if (this.validateDeploymentName(deploymentName)) {
        this.$store.commit('setDeploymentName', deploymentName)
      }
    },
    updateTab (tab) {
      this.currentTab = tab
    },
    validateDeploymentName (deploymentName) {
      return /^[a-zA-Z0-9-]{1,64}$/.test(deploymentName)
    }
  },
  components: {
    'u-deploy-info': DeployInfoComponent,
    'u-deploy-panel': DeployPanelComponent,
    'u-namespace-combobox': NamespaceComponent
  }
}
</script>

<style  lang="scss" scoped>
.commandTabs {
  .v-tab {
    text-transform: none !important;
  }
}

.command {
  display: flex;
  flex-flow: row nowrap;

  kbd {
    border-radius: 0;
    word-break: break-all;
    flex: 1 1 100%;
    max-height: 14rem;
    min-height: 7rem;
    padding: 1rem;
    overflow: auto;
    white-space: pre;

    a {
      color: white;
    }

    p {
      margin-bottom: 0px !important;
    }
  }

  .clipboard {
    flex: 0 0 auto;
    height: auto;
    margin: 0;
    min-width: 0;
    padding: 0 .5rem;
  }
}
</style>

<i18n>
{
  "en": {
    "title": "Choose your deployment method",
    "side_title": "Deploy",
    "headline": "Specify the deployment namespace and name.",
    "installation": "installation",
    "docker_placeholder": "Please fill in the fields to generate the docker command ...",
    "docker_compose_placeholder": "Please fill in the fields to generate the docker-compose ...",
    "v_app_placeholder": "Please fill in the fields to generate the vApp download link ...",
    "display_name": "{displayName}:",
    "connected": "connected",
    "warning_namespace": "Please note that there is already an infrastructure deployed in this namespace. Make sure that this is the behaviour that you seek. You can follow the deployment steps {here}.",
    "here": "here",
    "os": {
      "unix": "Unix",
      "windows": "Windows"
    },
    "id": {
      "label": "Enter the deployment name",
      "hint": "This will allow you to identify your nodes later on. Make sure your deployment name is memorable and meaningful."
    },
    "admin_password": {
      "label": "Initial Unryo Monitor Portal password",
      "hint": "On a new deployment, you can authenticate into the Unryo Monitor Portal as \"admin\" using this password. It doesn't have any effect during an upgrade."
    },
    "docker": {
      "title": "Docker",
      "label": "Run the following command on your host :",
      "namespace_warning": "The namespace that you have selected does not exist yet. Please create the namespace with the button above."
    },
    "docker-compose": {
      "title": "Docker-Compose"
    },
    "kubernetes": {
      "title": "Kubernetes"
    },
    "vApp": {
      "title": "VMware vCenter vApp",
      "download": "Download Unryo Monitor vApp",
      "info": "Information you will need at installation:",
      "token": "Unryo Token:",
      "vaultToken": "Vault Token:"
    },
    "trial": "Not available in trial version.",
    "error": {
      "deploymentName_required": "The deployment name is required.",
      "deploymentName_invalid": "It must be at most 64 characters long and only contain letters, numbers and dashes."
    },
    "tabs": {
      "info": {
        "title": "Info",
        "compose": {
          "title": "For Docker-Compose:",
          "points": {
            "copy": "Copy the content into a file inside your host named \"docker-compose.yml\".",
            "up": "Start your containers with the following command: \"docker-compose up -d\""
          },
          "doc": {
            "url": "quickstart",
            "id": "docker"
          }
        },
        "vApp": {
          "title": "For VMware vCenter vApp:",
          "points": {
            "click": "Click the provided link to download the vApp image."
          },
          "doc": {
            "url": "quickstart",
            "id": "vmware-vapp"
          }
        },
        "kubernetes": {
          "title": "For Kubernetes:",
          "doc": {
            "url": "quickstart",
            "id": "kubernetes-helm"
          }
        },
        "faq": "See {faq} FAQs"
      },
      "namespaces": {
        "title": "Namespaces",
        "text": "You have {numberOfDevice} components currently on the {namespace} namespace."
      },
      "detected": {
        "title": "Newly Detected",
        "text": "We will let you know when new components will start communicating with the Unryo Service …"
      }
    },
    "complete": "I'm done installing"
  }
}
</i18n>
